/**
 * Room chat panel (Sprint 3b): renders one lead room's LIVE chatter feed,
 * floating RUMORS (with an ad-gated fact-check), and the player reaction bar.
 * Pure rendering + event binding; all state changes go through dispatch.
 * Text-only, no emoji.
 */
import { COINS } from '../game/config/coins.js';
import { REACTION_CHOICES } from '../game/config/roomChatter.js';
import { NPC_NICKS } from '../game/config/comments.js';
import type { ChatMsg, RoomRuntime, Rumor } from '../game/leadRoomState.js';
import { t, tv } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { AdManager, getAdRewards } from '../monetization/AdManager.js';
import {
  getState, dispatch, markScreenDirty, requestRender, showToast,
} from './renderer.js';

const shownRumorHooks = new Set<string>();

function coinName(coinId: string): string {
  const cfg = COINS.find(c => c.id === coinId);
  return cfg ? t(cfg.nameKey) : coinId;
}

function nick(nickIdx: number): string {
  if (nickIdx < 0) return t('rc_me');
  return NPC_NICKS[nickIdx % NPC_NICKS.length];
}

/** Chatter line text: player reaction verbatim, echo pool, or category pool. */
function chatText(msg: ChatMsg): string {
  if (msg.reactionKey) return t(msg.reactionKey);
  if (msg.category === 'echo') {
    const pool = msg.mood ? `chat_echo_${msg.mood === 'neutral' ? 'neut' : msg.mood}` : 'chat_echo_neut';
    return t(`${pool}_${msg.textIdx + 1}`);
  }
  return t(`chat_${msg.category}_${msg.textIdx + 1}`);
}

function chatRowHtml(msg: ChatMsg): string {
  const mine = msg.nickIdx < 0 ? ' lrc-mine' : '';
  return `<div class="lrc-msg${mine}">
    <span class="lrc-nick">${nick(msg.nickIdx)}</span>
    <span class="lrc-text">${chatText(msg)}</span>
  </div>`;
}

function rumorVerdictHtml(rumor: Rumor): string {
  const resolved = rumor.factChecked;
  if (!resolved) return `<span class="lrc-rumor-badge lrc-rumor-unknown">?</span>`;
  return rumor.genuine
    ? `<span class="lrc-rumor-badge lrc-rumor-true">${t('rumor_true')}</span>`
    : `<span class="lrc-rumor-badge lrc-rumor-fake">${t('rumor_fake')}</span>`;
}

function rumorRowHtml(roomId: string, rumor: Rumor): string {
  const dirCls = rumor.direction === 1 ? 'ex-profit' : 'ex-loss';
  const arrow = rumor.direction === 1 ? '&#9650;' : '&#9660;';
  const text = tv(`rumor_${rumor.templateId}_${rumor.variant ?? 1}`, { coin: coinName(rumor.coinId) });
  let action = '';
  if (!rumor.factChecked) {
    const key = `rumor_check:${rumor.id}`;
    if (!shownRumorHooks.has(key)) {
      shownRumorHooks.add(key);
      dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'rumor_check', event: 'shown' } });
    }
    action = `<button class="hook-btn hook-btn-sm lrc-rumor-btn" data-rumor="${rumor.id}" data-room="${roomId}">
      <span class="hook-main">${t('rumor_check_btn')}</span>
      <span class="hook-sub">${t('rumor_check_sub')}</span>
    </button>`;
  }
  return `<div class="lrc-rumor">
    <div class="lrc-rumor-head">
      <span class="lrc-rumor-tag">${t('rumor_tag')}</span>
      <span class="lrc-rumor-text">${text}</span>
      <span class="lrc-rumor-dir ${dirCls}">${arrow}</span>
      ${rumorVerdictHtml(rumor)}
    </div>
    ${action}
  </div>`;
}

/** Full chat block for one room: rumors on top, then the live feed + reactions. */
export function roomChatHtml(roomId: string, room: RoomRuntime): string {
  const now = Date.now();
  let html = `<div class="lrc-panel">`;

  for (const r of room.rumors) html += rumorRowHtml(roomId, r);

  html += `<div class="lrc-feed">`;
  const visible = room.chat.filter(m => m.at <= now);
  if (visible.length === 0) {
    html += `<div class="lrc-empty">${t('lrc_quiet')}</div>`;
  } else {
    for (const m of visible) html += chatRowHtml(m);
  }
  html += `</div>`;

  html += `<div class="lrc-react-bar">`;
  for (let i = 0; i < REACTION_CHOICES.length; i++) {
    html += `<button class="lrc-react-btn" data-react="${i}" data-room="${roomId}">${t(REACTION_CHOICES[i].key)}</button>`;
  }
  html += `</div></div>`;

  return html;
}

async function onRumorCheck(roomId: string, rumorId: string) {
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'rumor_check', event: 'accepted' } });
  const result = await AdManager.showRewardedAd(getAdRewards().FACT_CHECK);
  if (result) {
    dispatch({ type: 'RUMOR_FACTCHECK', payload: { roomId, rumorId } });
    const room = getState().leadRooms.rooms[roomId];
    const rumor = room?.rumors.find(r => r.id === rumorId);
    playSfx('achievement');
    if (rumor) showToast(rumor.genuine ? t('rumor_true') : t('rumor_fake'));
  }
  markScreenDirty();
  requestRender();
}

function onReact(roomId: string, reactionIdx: number) {
  dispatch({ type: 'POST_ROOM_REACTION', payload: { roomId, reactionIdx } });
  playSfx('tap');
  markScreenDirty();
  requestRender();
}

/** Bind rumor fact-check + reaction buttons inside a rendered container. */
export function bindRoomChatEvents(container: HTMLElement) {
  container.querySelectorAll('[data-rumor]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const el = btn as HTMLElement;
      void onRumorCheck(el.dataset.room!, el.dataset.rumor!);
    });
  });
  container.querySelectorAll('[data-react]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const el = btn as HTMLElement;
      onReact(el.dataset.room!, Number(el.dataset.react));
    });
  });
}
